import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { NgxSpinnerService } from 'ngx-spinner';

import {
  FormModeEnum,
  NotificationService,
  ModalService,
  BackendService,
} from 'beyond-util';
import { SifarnikBase } from '../sifarnik-base/sifarnik-base';
import { BackendService as RtsBackendService } from '../../../services/backend.service';
import { EndpointTypes } from '../../../enums/enpoint-types.enum';
import { FormCode } from '../../../enums/form-code.enum';
import { FormName } from '../../../enums/form-name.enum';
import * as moment from 'moment';
import { EnumService } from '../../../services/enum.service';

@Component({
  selector: 'app-kursna-lista',
  templateUrl: './kursevi.component.html',
})
export class KurseviComponent extends SifarnikBase
  implements OnInit, OnDestroy {
  private _subscriptions: Array<Subscription> = [];
  private _data: Array<any>;
  public showDetails = false;

  constructor(
    protected _backendService: BackendService,
    protected _modalService: ModalService,
    protected _notificationService: NotificationService,
    private _rtsBackendService: RtsBackendService,
    private _enumService: EnumService,
    private _spinner: NgxSpinnerService
  ) {
    super(_backendService, _modalService, _notificationService);
  }

  ngOnInit() {
    setTimeout(() => {
      this._spinner.show();
      super.ngOnInit();
      this._enumService.fetchBanke(true);
      this._enumService.fetchValute();
    });
  }

  ngOnDestroy() {
    this._subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  changeName() {
    if (typeof this.order === 'undefined') {
      return;
    }

    if (this.order.hasOwnProperty('naziv')) {
      this.name = this.order.naziv;
    } else if (this.mode === FormModeEnum.Add) {
      this.name = 'Novi kurs';
    } else if (this.order.hasOwnProperty('id')) {
      this.name = 'Kurs';
    }
  }

  loadSettings() {
    this._rtsBackendService
      .fetchSettings(
        FormCode.MSKURS_KURS,
        FormName.PregledKursa,
        EndpointTypes.AllEntities,
        this.filterQuery
      )
      .subscribe(data => {
        this._spinner.hide();
        data.Settings.inlineEditing = false;

        if (!this.settingsTable) {
          this.setSettings(data.Settings);
        }
        this._data = data.ServiceData;
        this.setRbr(this._data);
        this.setId(this._data);
        this.orderList = this._data;
        this.setAllFields();
      });
  }

  onTableActionFilter(event: any) {
    this._spinner.show();
    super.onTableActionFilter(event);
  }

  setSettings(allSettings) {
    super.setSettings(allSettings);
    this.toggleFields();
    this.setDecimalCount();
  }

  toggleFields() {
    if (this.order) {
      const enblDatVal: boolean = this.order.Datum && this.order.ValutaId;
      this.settingsTable.getFieldByName('Banka').editable = <boolean>enblDatVal;

      if (this.order.Banka) {
        const enblKurs = this.order.Banka.Id !== 1;
        this.settingsTable.getFieldByName('KupovniKurs').editable = enblKurs;
        this.settingsTable.getFieldByName('ProdajniKurs').editable = enblKurs;
        this.settingsTable.getFieldByName('SrednjiKurs').editable = enblKurs;
      }
    }
  }

  setDecimalCount() {
    this.settingsTable.getFieldByName('KupovniKurs').decimalCount = 4;
    this.settingsTable.getFieldByName('ProdajniKurs').decimalCount = 4;
    this.settingsTable.getFieldByName('SrednjiKurs').decimalCount = 4;
  }

  addOrder() {
    this._spinner.show();
    const payload = { ...this.order };
    this._rtsBackendService
      .save(FormCode.MSKURS_KURS, FormName.PregledKursa, payload)
      .subscribe(() => {
        this.loadSettings();
      });
  }

  onDeleteConfirm() {
    this._spinner.show();
    const payload = { ...this.order };

    this._rtsBackendService
      .delete(FormCode.MSKURS_KURS, FormName.PregledKursa, payload.Id)
      .subscribe(() => {
        this._notificationService.openNoty('Uspešno brisanje');
        this.loadSettings();
      });
  }

  onTableInlineSave(event) {
    this.addOrder();
  }

  onValueChange(event) {
    if (event.newValue) {
      // datum
      if (event.newValue._isAMomentObject) {
        this.order.Datum = moment(event.newValue).toISOString();
      }

      this.toggleFields();

      switch (event.fieldMap.fieldName) {
        case 'Banka':
          if (event.newValue.Naziv === 'Narodna Banka Srbije') {
            this.fetchNBSKurs();
          } else if (
            !event.newValue.Naziv ||
            (event.oldValue && event.newValue.Naziv !== event.oldValue.Naziv)
          ) {
            this.clearKurs();
          }
          break;
        case 'Datum':
          if (
            this.order.Banka &&
            this.order.Banka.Naziv === 'Narodna Banka Srbije'
          ) {
            if (!this.validateDatum()) {
              this.clearKurs();
            } else {
              this.fetchNBSKurs();
            }
          }
          break;
        case 'ValutaId':
          if (
            this.order.Banka &&
            this.order.Banka.Naziv === 'Narodna Banka Srbije'
          ) {
            this.fetchNBSKurs();
          } else if (event.oldValue && event.newValue !== event.oldValue) {
            this.clearKurs();
          }
          break;
      }
    }
  }

  validateDatum(): boolean {
    const todayString = moment().format('DD.MM.YYYY');
    const datumString = moment(this.order.Datum).format('DD.MM.YYYY');
    const valid = !moment(datumString).isAfter(moment(todayString));

    if (!valid) {
      this._notificationService.notyError(
        `Maxmimalan datum za NBS kurs je ${moment().format('DD.MM.YYYY')}.`
      );
    }

    return valid;
  }

  private clearKurs() {
    this.order.ProdajniKurs = null;
    this.order.KupovniKurs = null;
    this.order.SrednjiKurs = null;
  }

  fetchNBSKurs() {
    if (this.validateDatum()) {
      this._rtsBackendService
        .fetchSettings(
          FormCode.MSKURS_NBS,
          FormName.PregledKursa,
          EndpointTypes.AllEntities,
          `?datum=${this.order.Datum}&valutaId=${this.order.ValutaId}`
        )
        .subscribe(data => {
          this.setKurs(data);
        });
    }
  }

  setKurs(data: any) {
    this.order.ProdajniKurs = data.ServiceData.ProdajniKurs;
    this.order.KupovniKurs = data.ServiceData.KupovniKurs;
    this.order.SrednjiKurs = data.ServiceData.SrednjiKurs;
  }

  onTableActionAdd() {
    this.toggleFields();
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/sifarnici/kursevi/kursevi.component.ts